import React, { useCallback, useEffect, useState } from 'react';
import { useQuery } from '@apollo/client';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth';
import { useUserRestrictions } from '../../auth/user-restrictions';
import { useTeamInfo } from '../../team-id-context';
import { usePartyIds, queries } from '../../api';
import { hasUserTeamOwnedOrganization } from '../../api/queries';
import { get as getLatestTeamId, set as setLatestTeamId } from '../../utils/latest-team-id';
import { usePermissions } from '../../contexts/PermissionsContext';
import { useDocumentState } from '../../contexts/DocumentContext';
import { paths } from '../../routing';
import { SubscriptionRequiredModal } from '../../pages';
import SignOutConfirmationModal from './SignOutConfirmationModal';
import Logotext from '../logotext';

// Icons
import { ReactComponent as DealsIcon } from '../../shared/icons/folder_lock_light_icon.svg';
import { ReactComponent as TeamIcon } from '../../shared/icons/team-icon.svg';
import { ReactComponent as HelpIcon } from '../../shared/icons/help-icon.svg';
import { ReactComponent as SignoutIcon } from '../../shared/icons/sign-out-icon.svg';
import { ReactComponent as TemplateIcon } from '../../shared/icons/icon-template.svg';
import { ReactComponent as ClausesIcon } from '../../shared/icons/icon-clauses.svg';
import { ReactComponent as SettingsIcon } from '../../shared/icons/gear-icon.svg';
import { ReactComponent as TrackerIcon } from '../../shared/icons/clock-icon.svg';
import { ReactComponent as DatabaseIcon } from '../../shared/icons/database-icon.svg';

// Components
import { NavItem } from './NavItem';
import { NavSubmenu } from './NavSubmenu';

// Styles
import styles from './desktop-nav.module.scss';

interface DesktopNavProps {
  className?: string;
}

const DesktopNav: React.FC<DesktopNavProps> = ({ className }) => {
  const [{ user }, { logout }] = useAuth();
  const navigate = useNavigate();
  const { resetDocumentState } = useDocumentState();
  const location = useLocation();
  const { restrictionsLoading, hasAccessToTemplates, hasAccessToClauses, canCreateTemplates } = useUserRestrictions();
  const { setTeamPermissions } = usePermissions();

  // State
  const [isExpanded, setIsExpanded] = useState(false);
  const [openSubmenu, setOpenSubmenu] = useState<string | null>(null);
  const [showSubscriptionRequiredModal, setSubscriptionRequiredModal] = useState(false);
  const [showSignOutModal, setShowSignOutModal] = useState(false);
  // Team data
  const teamId = useTeamInfo().teamId ?? getLatestTeamId(user?.id);
  const { loading: partyIdsLoading, partyIds } = usePartyIds();
  const { loading: teamLoading, data: teamData } = useQuery(queries.getTeamById, {
    skip: teamId == null,
    variables: { teamId },
  });

  const loading = partyIdsLoading || teamLoading || restrictionsLoading;
  const partyId = loading ? null : (teamData?.teamById.party.id ?? partyIds.tenant);

  // Update latest team ID
  useEffect(() => {
    if (teamId == null) return;
    setLatestTeamId(user?.id, teamId);
  }, [teamId, partyId, partyIds, user]);

  // Auto-close submenu when nav collapses
  useEffect(() => {
    if (!isExpanded && openSubmenu) {
      setOpenSubmenu(null);
    }
  }, [isExpanded, openSubmenu]);

  // Handlers
  const handleNavItemClick = (itemId: string) => {
    if (itemId === 'settings') {
      setOpenSubmenu(openSubmenu === itemId ? null : itemId);
    }
  };

  const handleLogout = useCallback(() => {
    setShowSignOutModal(true);
  }, []);

  const confirmSignOut = useCallback(() => {
    setTeamPermissions([]);
    logout({}).then(() => {
      navigate(paths.auth.login);
      resetDocumentState();
    });
    setShowSignOutModal(false);
  }, [logout, resetDocumentState, navigate, setTeamPermissions]);

  const cancelSignOut = useCallback(() => {
    setShowSignOutModal(false);
  }, []);

  // Check active states
  const isActive = (pathPattern: RegExp | string): boolean => {
    if (typeof pathPattern === 'string') {
      return location.pathname === pathPattern;
    }
    return pathPattern.test(location.pathname);
  };

  // Get organization data for settings submenu
  const {
    data: organizationsData,
    error: organizationsDataLoadingError,
  } = useQuery(hasUserTeamOwnedOrganization, {
    skip: !teamId,
    variables: { teamId },
  });

  // Check if current user is team admin and team owns organization
  const { adminUser } = useTeamInfo();
  const isTeamAdmin = user?.id && adminUser?.id && user.id === adminUser.id;
  const hasOrganization = organizationsData?.teamById?.organization?.id;
  const shouldShowOrgRequests = isTeamAdmin && hasOrganization && !organizationsDataLoadingError;

  // Settings submenu items
  const settingsItems = [
    {
      label: 'Organizations',
      href: paths.settings.organizations,
      params: { teamId },
    },
    ...(shouldShowOrgRequests
      ? [
          {
            label: 'Org Requests',
            href: paths.settings.requests,
            params: { teamId },
          },
        ]
      : []),
    {
      label: 'Checklists',
      href: paths.settings.checklistTemplates,
      params: { teamId },
    },
    // Contact Fields - Premium feature
    canCreateTemplates
      ? {
          label: 'Contact Fields',
          href: paths.settings.contactFields,
          params: { teamId },
        }
      : {
          label: 'Contact Fields',
          onClick: () => setSubscriptionRequiredModal(true),
        },
    {
      label: 'Account',
      href: paths.account,
      params: {},
    },
  ];

  return (
    <>
      <nav
        className={`${styles.nav} ${className || ''}`}
        data-expanded={isExpanded}
        onMouseEnter={() => setIsExpanded(true)}
        onMouseLeave={() => setIsExpanded(false)}
      >
        {/* Logo */}
        <div className={styles.logo}>
          <div className={styles.logoContainer}>
            <Logotext isFull />
          </div>
        </div>

        {/* Navigation Items */}
        <div className={styles.navItems}>
          {teamId ? (
            <NavItem
              icon={DealsIcon}
              label="Deals"
              href={paths.deals.landing}
              hrefParams={{ teamId }}
              isActive={isActive(paths.deals._catchallRegex)}
            />
          ) : (
            <NavItem icon={DealsIcon} label="Deals" href={paths.home} />
          )}

          {/* Tracker - Premium feature for critical event tracking */}
          {canCreateTemplates ? (
            <NavItem
              icon={TrackerIcon}
              label="Tracker"
              href={paths.tracker.landing}
              hrefParams={{ teamId }}
              isActive={isActive(paths.tracker._catchallRegex)}
            />
          ) : (
            <NavItem icon={TrackerIcon} label="Tracker" onClick={() => setSubscriptionRequiredModal(true)} />
          )}

          {/* Data - Premium feature for extracted document data */}
          {canCreateTemplates ? (
            <NavItem
              icon={DatabaseIcon}
              label="Data"
              href={paths.data.landing}
              hrefParams={{ teamId }}
              isActive={isActive(paths.data._catchallRegex)}
            />
          ) : (
            <NavItem icon={DatabaseIcon} label="Data" onClick={() => setSubscriptionRequiredModal(true)} />
          )}

          {hasAccessToTemplates ? (
            <NavItem
              icon={TemplateIcon}
              label="Templates"
              href={paths.templates.landing}
              hrefParams={{ teamId }}
              isActive={isActive(paths.templates._catchallRegex)}
            />
          ) : (
            <NavItem icon={TemplateIcon} label="Templates" onClick={() => setSubscriptionRequiredModal(true)} />
          )}

          {hasAccessToClauses ? (
            <NavItem
              icon={ClausesIcon}
              label="Clauses"
              href={paths.clauses.landing}
              hrefParams={{ teamId }}
              isActive={isActive(paths.clauses._catchallRegex)}
            />
          ) : (
            <NavItem icon={ClausesIcon} label="Clauses" onClick={() => setSubscriptionRequiredModal(true)} />
          )}

          <NavItem
            icon={TeamIcon}
            label="People"
            href={paths.team.landing}
            hrefParams={{ teamId }}
            isActive={isActive(paths.team._catchallRegex)}
          />

          {/* Settings with submenu */}
          <div className={styles.settingsGroup}>
            <NavItem
              icon={SettingsIcon}
              label="Settings"
              hasSubmenu
              submenuOpen={openSubmenu === 'settings'}
              onClick={() => handleNavItemClick('settings')}
              isActive={isActive(paths.settings._catchallRegex)}
            />
            <NavSubmenu
              items={settingsItems}
              isOpen={openSubmenu === 'settings'}
              onItemClick={() => setOpenSubmenu(null)}
            />
          </div>

          <NavItem icon={HelpIcon} label="Help" href="/help" external />

          {/* Sign out - positioned at bottom */}
          <div className={styles.signoutWrapper}>
            <NavItem icon={SignoutIcon} label="Sign Out" onClick={handleLogout} />
          </div>
        </div>
      </nav>

      {/* Modals */}
      <SubscriptionRequiredModal
        isOpen={showSubscriptionRequiredModal}
        onClose={() => setSubscriptionRequiredModal(false)}
      />
      <SignOutConfirmationModal show={showSignOutModal} onClose={cancelSignOut} onConfirm={confirmSignOut} />
    </>
  );
};

export default DesktopNav;
